import { useMemo } from "react";
import { matchRoutes, Outlet, useLocation } from "react-router";
import { motion, AnimatePresence } from "framer-motion";

import { BrandLoader } from "@/client/components/brand-loader";
import { useAuthProviders } from "@/client/services/auth/providers";
import { AuthPanelBg } from "./_components/auth-panel-bg";

const authRoutes = [{ path: "client/auth/login" }, { path: "client/auth/register" }];

export const AuthLayout = () => {
  const location = useLocation();
  const { providers } = useAuthProviders();
  useMemo(() => matchRoutes(authRoutes, location) !== null, [location]);

  if (!providers) return <BrandLoader fullScreen text="Préparation de la connexion…" />;

  const pageName = location.pathname.split("/").pop() || "login";
  const imageSrc = `/auth/${pageName}.webp`;

  return (
    <div data-space="client" className="flex h-screen w-screen overflow-hidden">
      {/* ════════════════════════════════════════
          LEFT PANEL — fond animé + illustration
          ════════════════════════════════════════ */}
      <motion.div
        className="relative hidden shrink-0 items-center justify-center overflow-hidden lg:flex lg:w-[52%]"
        style={{
          background: "linear-gradient(145deg, #f0ecfb 0%, #e8e2f8 40%, #ede9f9 70%, #eaf6f1 100%)",
        }}
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Fond animé — orbs, grille, waves, particules */}
        <AuthPanelBg />

        {/* Logo original SVG — coin supérieur gauche */}
        <div className="absolute left-10 top-8 z-20">
          <img
            src="/logo/mon-cv-pro_logo.svg"
            alt="Mon CV Pro"
            className="h-20 w-auto object-contain drop-shadow-sm"
          />
        </div>

        {/* Illustration centrale — avec AnimatePresence pour transition entre pages */}
        <AnimatePresence mode="wait">
          <motion.img
            key={imageSrc}
            src={imageSrc}
            alt="Illustration"
            className="relative z-10 w-[88%] max-w-[540px] object-contain"
            style={{
              filter:
                "drop-shadow(0 24px 48px rgba(71,44,179,0.18)) drop-shadow(0 8px 16px rgba(0,0,0,0.08))",
            }}
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.03, y: -10 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        </AnimatePresence>

        {/* Badge de confiance — bas du panel */}
        <motion.div
          className="absolute bottom-8 left-0 right-0 z-20 flex justify-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-4 rounded-2xl border border-white/80 bg-white/60 px-6 py-3 shadow-sm backdrop-blur-md">
            {[
              { value: "900K+", label: "Utilisateurs" },
              { value: "200K+", label: "CV créés" },
              { value: "4.2★", label: "Note moyenne" },
            ].map(({ value, label }) => (
              <div key={label} className="flex flex-col items-center">
                <span className="text-sm font-bold leading-none text-[#472cb3]">{value}</span>
                <span className="mt-0.5 text-[10px] leading-none text-gray-400">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* ════════════════════════════════════════
          RIGHT PANEL — formulaire
          ════════════════════════════════════════ */}
      <motion.div
        className="relative flex w-full shrink-0 flex-col justify-center overflow-y-auto bg-white lg:w-[48%]"
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.12 }}
      >
        {/* Theme switch */}
        <div className="absolute right-6 top-5 z-20"></div>

        {/* Logo — mobile uniquement */}
        <div className="absolute left-6 top-5 z-20 lg:hidden">
          <img
            src="/logo/mon-cv-pro_logo.svg"
            alt="Mon CV Pro"
            className="h-12 w-auto object-contain"
          />
        </div>

        {/* Formulaire centré, max-w strict, aucun scroll */}
        <div className="mx-auto w-full max-w-[420px] px-6 sm:px-0">
          <Outlet />
        </div>
      </motion.div>
    </div>
  );
};
